import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {Sort} from 'search/SearchConstants';
import * as Colors from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';

const styles = StyleSheet.create({
  toggleIcon: {color: Colors.GRAY_DARK, cursor: 'pointer', fontSize: 14, margin: 7, flex: 'none'},
});

function getDisplaySettingsOption(
  isCompactDisplay: boolean,
  onSetIsCompactDisplay: (isCompact: boolean) => void
): JSX.Element {
  return (
    <Option icon="table">
      Display density
      <Menu>
        <Option
          icon="comfortable-view"
          isSelected={!isCompactDisplay}
          onSelect={() => {
            onSetIsCompactDisplay(false);
          }}
        >
          Comfortable
        </Option>
        <Option
          icon="compact-view"
          isSelected={isCompactDisplay}
          onSelect={() => {
            onSetIsCompactDisplay(true);
          }}
        >
          Compact
        </Option>
      </Menu>
    </Option>
  );
}

function getPageSizeOption(
  availablePageSizes: Array<number>,
  selectedPageSize: number,
  onSetPageSize: (pageSize: number) => void
): JSX.Element | null {
  return (
    <Option
      icon="format-left"
      dataTestAttrs={{component: 'ListingSettingsDropdown', element: 'menu', key: 'resultsPerPage'}}
    >
      Results per page
      <Menu>
        {availablePageSizes.map((pageSize) => (
          <Option
            key={pageSize}
            isSelected={pageSize === selectedPageSize}
            onSelect={() => {
              onSetPageSize(pageSize);
            }}
            dataTestAttrs={{component: 'ListingSettingsDropdown', element: 'menu', key: `${pageSize}`}}
          >
            {pageSize}
          </Option>
        ))}
      </Menu>
    </Option>
  );
}

export interface SortItem {
  sortKey: string;
  reverse: boolean;
  sortKeyDisplayName: string;
  reverseDisplayName: string | null;
}

export function getSortMenuOption(
  availableSortOptions: Array<Array<SortItem>>,
  sorts: Array<Sort>,
  onSetSort: (sortKey: string, reverse: boolean) => void
): JSX.Element {
  return (
    <Option
      icon="arrow-down"
      dataTestAttrs={{component: 'ListingSettingsDropdown', element: 'menu', key: 'sort'}}
    >
      Sort by
      <Menu>
        {availableSortOptions.map((sortItemSection, index) => (
          <Section key={index.toString()}>
            {sortItemSection.map((sortItem) => {
              const key = `sort_${sortItem.sortKey}_${sortItem.reverse}`;
              return (
                <Option
                  value={{sortKey: sortItem.sortKey, reverse: sortItem.reverse}}
                  isSelected={
                    // Some listings add hidden sorts, such as starred, so find the position of a selected
                    // key rather than assuming it will be the first key.
                    sorts.findIndex(
                      (sort) => sort.key === sortItem.sortKey && sort.reverse === sortItem.reverse
                    ) !== -1
                  }
                  onSelect={() => {
                    onSetSort(sortItem.sortKey, sortItem.reverse);
                  }}
                  metadata={sortItem.reverseDisplayName}
                  key={key}
                  dataTestAttrs={{
                    component: 'ListingSettingsDropdown',
                    element: 'menu',
                    key,
                  }}
                >
                  {sortItem.sortKeyDisplayName}
                </Option>
              );
            })}
          </Section>
        ))}
      </Menu>
    </Option>
  );
}

export interface ListingSettingsDropdownProps {
  pageSizeConfig?: {
    availablePageSizes: Array<number>;
    pageSize: number;
    onSetPageSize: (pageSize: number) => void;
  };
  displaySettingsConfig?: {
    isCompact: boolean;
    setIsCompactDisplay: (isCompact: boolean) => void;
  };
  sortConfig?: {
    availableSortOptions: Array<Array<SortItem>>;
    sorts: Array<Sort>;
    onSetSort: (sortKey: string, reverse: boolean) => void;
  };
  iconAltText?: string;
}

/**
 * A dropdown for listing options: page size, sort, etc.
 */
export default function ListingSettingsDropdown(props: ListingSettingsDropdownProps): JSX.Element {
  return (
    <Dropdown
      className="_test-settings-dropdown"
      menu={
        <Menu width={Menu.WIDTHS.LARGE} height={Menu.HEIGHTS.MEDIUM}>
          {props.sortConfig &&
            getSortMenuOption(
              props.sortConfig.availableSortOptions,
              props.sortConfig.sorts,
              props.sortConfig.onSetSort
            )}
          {props.displaySettingsConfig &&
            getDisplaySettingsOption(
              props.displaySettingsConfig.isCompact,
              props.displaySettingsConfig.setIsCompactDisplay
            )}
          {props.pageSizeConfig &&
            getPageSizeOption(
              props.pageSizeConfig.availablePageSizes,
              props.pageSizeConfig.pageSize,
              props.pageSizeConfig.onSetPageSize
            )}
        </Menu>
      }
    >
      <BenchlingIcon className={css(styles.toggleIcon)} icon="filter" altText={props.iconAltText} />
    </Dropdown>
  );
}
